import React, { useCallback, useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  Modal,
  TextField,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Pagination,
  Grid,
} from '@mui/material';
import { FaPlus, FaTrash, FaEdit, FaEye } from 'react-icons/fa';
import config from '../config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const server_URL = config.SERVER_URL;

const defaultSegmentForm = {
  id: null,
  name: '',
  customer_id: '',
  tag_ids: [],
  event_ids: [],
  start_date: '',
  end_date: '',
  date_range_apply_to: 'Contact create',
  conditional_statements: [],
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  width: '95%',
  maxWidth: 1200,
  maxHeight: '90vh',
  overflowY: 'auto',
};

const Segments = () => {
  const [segments, setSegments] = useState([]);
  const [segmentModalOpen, setSegmentModalOpen] = useState(false);
  const [segmentForm, setSegmentForm] = useState(defaultSegmentForm);

  // Additional state for customer management
  const [customers, setCustomers] = useState([]);
  const [filterCustomers, setFilterCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);
  const [tags, setTags] = useState([]); // Available tags for selected customer
  const [events, setEvents] = useState([]); // Available events for selected customer
  const [contactListModalOpen, setContactListModalOpen] = useState(false);
  const [contactListData, setContactListData] = useState([]);
  const [contactListLoading, setContactListLoading] = useState(false);
  const [contactListCurrentPage, setContactListCurrentPage] = useState(1);
  const [contactListTotalPages, setContactListTotalPages] = useState(1);
  const [contactListTotalRecords, setContactListTotalRecords] = useState(0);
  const [selectedSegmentForContactList, setSelectedSegmentForContactList] = useState(null);
  const contactListItemsPerPage = 100; // Show 100 records per page

  // Initialize user role and ID on mount
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setLoggedUserId(userData.id || null);
    // Load customers on mount for use in add/edit modals
    fetchCustomers();
  }, []);

  // Fetch customer accounts
  const fetchCustomers = async (userDataArg) => {
    try {
      const userData = userDataArg || JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: 'list',
          user_id: userData.id || null,
          role_id: userData.role_id || null
        }),
      });
      if (!response.ok) throw new Error('Failed to fetch customers');

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomers(data.data);
        // For filter dropdown: role 1 sees all, role 2 sees only logged customer
        if (userData.role_id === 1) {
          setFilterCustomers(data.data);
        } else if (userData.role_id === 2 && userData.id) {
          setFilterCustomers(data.data.filter(customer => customer.id === userData.id));
        }
        return data.data; // Return customers for use in calling functions
      } else {
        setCustomers([]);
        setFilterCustomers([]);
        return [];
      }
    } catch (error) {
      setCustomers([]);
      console.error('Error fetching customers:', error);
      toast.error('Unable to load customer list');
      return [];
    }
  };

  const fetchSegmentsFromServer = useCallback(async (filterCustomerId = null) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'list',
        user_id: userData.id || null,
        role_id: userData.role_id || null,
      };
      
      // Priority: explicit customer filter > role-based filtering
      // If role is 2, filter by logged user's customer_id (unless explicit filter is provided)
      if (filterCustomerId !== null && filterCustomerId !== '') {
        const customerIdValue = String(filterCustomerId) === '0' ? 0 : parseInt(filterCustomerId, 10);
        requestBody.customer_id = customerIdValue;
        console.log('Filtering by customer_id:', customerIdValue);
      } else if (userData.role_id === 2 && userData.id) {
        // For role 2 users, automatically filter by their own customer_id
        requestBody.customer_id = userData.id;
        console.log('Role 2 user - filtering by own customer_id:', userData.id);
      }
      
      console.log('Request body:', requestBody);
      
      const response = await fetch(`${server_URL}/api/segments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch segments from server');
      }

      const data = await response.json();
      if (data.status === '1' && Array.isArray(data.data)) {
        setSegments(data.data);
      } else {
        console.error('Error response from server:', data);
        toast.error(data.error || 'Unable to load segments from server');
        setSegments([]);
      }
    } catch (error) {
      console.error('Error fetching segments from server:', error);
      toast.error(`Error: ${error.message || 'Unable to load segments from server'}`);
      setSegments([]);
    }
  }, []);

  useEffect(() => {
    fetchSegmentsFromServer();
  }, [fetchSegmentsFromServer]);

  const handleFilter = () => {
    fetchSegmentsFromServer(customerFilter);
  };

  const handleClearFilter = () => {
    setCustomerFilter('');
    // For role 2 users, fetchSegmentsFromServer will automatically filter by their own customer_id
    fetchSegmentsFromServer();
  };

  const fetchTags = async (customerId) => {
    if (!customerId || customerId === 0) {
      setTags([]);
      return;
    }
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/tags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'list',
          user_id: userData.id || null,
          role_id: userData.role_id || null,
          customer_id: customerId,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setTags(Array.isArray(data.data) ? data.data : []);
      } else {
        setTags([]);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]);
    }
  };

  const fetchEvents = async (customerId = null) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'get_events',
        user_role: userData.role_id,
        user_id: userData.id,
      };
      
      // Add customer_id if provided (for filtering events)
      if (customerId) {
        requestBody.customer_id = customerId;
      }
      
      const response = await fetch(server_URL + '/api/trigger_automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setEvents(data.data || []);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    }
  };

  const handleSegmentFormChange = (field, value) => {
    setSegmentForm((prev) => {
      const updated = { ...prev, [field]: value };
      
      // If customer_id changes, fetch tags and events for that customer
      if (field === 'customer_id') {
        if (value && value !== 0) {
          fetchTags(value);
          fetchEvents(value);
          // Clear tag_ids and event_ids when customer changes
          updated.tag_ids = [];
          updated.event_ids = [];
        } else {
          setTags([]);
          setEvents([]);
          updated.tag_ids = [];
          updated.event_ids = [];
        }
      }
      
      return updated;
    });
  };

  const resetSegmentForm = () => {
    // Only pre-select customer if only 1 and you know from state
    setSegmentForm((prev) => ({
      ...defaultSegmentForm,
      customer_id: (customers && customers.length === 1) ? customers[0].id : ''
    }));
  };

  const addConditionalStatement = () => {
    setSegmentForm((prev) => ({
      ...prev,
      conditional_statements: [
        ...(prev.conditional_statements || []),
        {
          action_type: '', // Has Opened / Has Clicked
          aggregation: '', // Count / Sum / Avg / Percentage
          operator: '', // equal to / less than / greater than / less than equal to / greater than equal to
          value: '' // integer value
        }
      ]
    }));
  };

  const removeConditionalStatement = (index) => {
    setSegmentForm((prev) => ({
      ...prev,
      conditional_statements: prev.conditional_statements.filter((_, i) => i !== index)
    }));
  };

  const updateConditionalStatement = (index, field, value) => {
    setSegmentForm((prev) => {
      const updated = [...(prev.conditional_statements || [])];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return {
        ...prev,
        conditional_statements: updated
      };
    });
  };

  const openNewSegmentModal = async () => {
    // Always refresh customers on open
    const fetchedCustomers = await fetchCustomers();
    
    // Reset form and preselect customer if only one option
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const customerId = (fetchedCustomers.length === 1) ? fetchedCustomers[0].id : (userData.role_id === 2 && userData.id ? userData.id : '');
    
    setSegmentForm({
      ...defaultSegmentForm,
      customer_id: customerId
    });
    
    // Fetch tags and events if customer is selected
    if (customerId && customerId !== 0) {
      await Promise.all([
        fetchTags(customerId),
        fetchEvents(customerId)
      ]);
    } else {
      setTags([]);
      setEvents([]);
    }
    
    setSegmentModalOpen(true);
  };

  const openEditSegmentModal = async (segment) => {
    // Ensure customers are loaded before opening edit modal
    await fetchCustomers();
    
    // Find the matching customer id if present
    const customerId = segment.customer_id !== undefined && segment.customer_id !== null ? segment.customer_id : '';
    
    // Fetch tags and events FIRST (before setting form data)
    // This ensures the Select options are available when we set the selected values
    if (customerId && customerId !== 0) {
      await Promise.all([
        fetchTags(customerId),
        fetchEvents(customerId)
      ]);
    } else {
      setTags([]);
      setEvents([]);
    }
    
    // Parse tag_ids and event_ids from JSON if they exist
    let tagIds = [];
    let eventIds = [];
    try {
      if (segment.tag_ids) {
        tagIds = typeof segment.tag_ids === 'string' ? JSON.parse(segment.tag_ids) : segment.tag_ids;
        tagIds = Array.isArray(tagIds) ? tagIds : [];
        // Convert to integers to match tag.id types
        tagIds = tagIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
      }
      if (segment.event_ids) {
        eventIds = typeof segment.event_ids === 'string' ? JSON.parse(segment.event_ids) : segment.event_ids;
        eventIds = Array.isArray(eventIds) ? eventIds : [];
        // Convert to integers to match event.id types
        eventIds = eventIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
      }
    } catch (error) {
      console.error('Error parsing tag_ids or event_ids:', error);
    }
    
    // Format dates for input fields (YYYY-MM-DD)
    const formatDateForInput = (date) => {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    };
    
    // Parse conditional_statements from JSON if they exist
    let conditionalStatements = [];
    try {
      if (segment.conditional_statements) {
        conditionalStatements = typeof segment.conditional_statements === 'string' 
          ? JSON.parse(segment.conditional_statements) 
          : segment.conditional_statements;
        conditionalStatements = Array.isArray(conditionalStatements) ? conditionalStatements : [];
      }
    } catch (error) {
      console.error('Error parsing conditional_statements:', error);
      conditionalStatements = [];
    }

    const formData = {
      id: segment.id,
      name: segment.name || '',
      customer_id: customerId,
      tag_ids: tagIds,
      event_ids: eventIds,
      start_date: formatDateForInput(segment.start_date),
      end_date: formatDateForInput(segment.end_date),
      date_range_apply_to: segment.date_range_apply_to || 'Contact create',
      conditional_statements: conditionalStatements,
    };
    
    // Set form data AFTER fetching tags/events
    setSegmentForm(formData);
    setSegmentModalOpen(true);
  };

  const handleSegmentSubmit = (event) => {
    event.preventDefault();
    // Validate customer selection
    const customerId = segmentForm.customer_id;
    if (customerId === '' || customerId === null || customerId === undefined) {
      toast.error('Please select a customer');
      return;
    }
    if (!segmentForm.name.trim()) {
      toast.error('Segment name is required');
      return;
    }

    const saveToServer = async (segmentPayload) => {
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const isUpdate = !!segmentForm.id;
        // Send to API
        const requestBody = {
          method: isUpdate ? 'update' : 'add',
          id: isUpdate ? segmentPayload.id : undefined,
          name: segmentPayload.name,
          user_id: userData.id || null,
          customer_id: segmentPayload.customer_id,
          tag_ids: segmentPayload.tag_ids || [],
          event_ids: segmentPayload.event_ids || [],
          start_date: segmentPayload.start_date || null,
          end_date: segmentPayload.end_date || null,
          date_range_apply_to: segmentPayload.date_range_apply_to || 'Contact create',
          conditional_statements: segmentPayload.conditional_statements || [],
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        };

        const response = await fetch(`${server_URL}/api/segments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        const data = await response.json();
        if (data.status !== '1') {
          toast.error(data.error || 'Failed to save segment to server');
          return;
        }
        
        if (!isUpdate && data.id) {
          segmentPayload.id = data.id;
        }

        if (data.status === '1') {
          fetchSegmentsFromServer();
          toast.success(segmentForm.id ? 'Segment updated' : 'Segment saved');
          setSegmentModalOpen(false);
          resetSegmentForm();
        }
      } catch (error) {
        console.error('Error saving segment to server:', error);
        toast.error('Failed to save segment to server');
      }
    };

    // Ensure tag_ids and event_ids are arrays of integers
    const tagIds = Array.isArray(segmentForm.tag_ids) 
      ? segmentForm.tag_ids.map(id => parseInt(id, 10)).filter(id => !isNaN(id))
      : [];
    const eventIds = Array.isArray(segmentForm.event_ids)
      ? segmentForm.event_ids.map(id => parseInt(id, 10)).filter(id => !isNaN(id))
      : [];
    
    // Filter out empty conditional statements
    const conditionalStatements = (segmentForm.conditional_statements || []).filter(
      (cs) => cs.action_type && cs.aggregation && cs.operator && cs.value !== ''
    );

    const payload = {
      id: segmentForm.id || Date.now(),
      name: segmentForm.name.trim(),
      customer_id: segmentForm.customer_id,
      tag_ids: tagIds,
      event_ids: eventIds,
      start_date: segmentForm.start_date || null,
      end_date: segmentForm.end_date || null,
      date_range_apply_to: segmentForm.date_range_apply_to || 'Contact create',
      conditional_statements: conditionalStatements,
    };

    saveToServer(payload);
  };

  const handleViewContactList = async (segment, page = 1) => {
    if (!segment.sqlquery) {
      toast.error('No SQL query available for this segment');
      return;
    }
    
    setSelectedSegmentForContactList(segment);
    setContactListCurrentPage(page);
    setContactListLoading(true);
    setContactListModalOpen(true);
    
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${server_URL}/api/segments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_contact_list',
          segment_id: segment.id,
          sqlquery: segment.sqlquery,
          page: page,
          per_page: contactListItemsPerPage,
          user_id: userData.id || null,
          role_id: userData.role_id || null,
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        setContactListData(data.data || []);
        if (data.pagination) {
          setContactListTotalPages(data.pagination.total_pages || 1);
          setContactListTotalRecords(data.pagination.total_records || 0);
        }
      } else {
        toast.error(data.error || 'Failed to fetch contact list');
        setContactListData([]);
      }
    } catch (error) {
      console.error('Error fetching contact list:', error);
      toast.error('An error occurred while fetching contact list');
      setContactListData([]);
    } finally {
      setContactListLoading(false);
    }
  };

  const handleContactListPageChange = (event, value) => {
    if (selectedSegmentForContactList) {
      handleViewContactList(selectedSegmentForContactList, value);
    }
  };

  const handleCloseContactListModal = () => {
    setContactListModalOpen(false);
    setContactListData([]);
    setSelectedSegmentForContactList(null);
    setContactListCurrentPage(1);
    setContactListTotalPages(1);
    setContactListTotalRecords(0);
  };

  const handleDeleteSegment = async (segmentId) => {
    const previousSegments = segments;
    setSegments((prev) => prev.filter((seg) => seg.id !== segmentId));

    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${server_URL}/api/segments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'delete',
          id: segmentId,
          user_id: userData.id || null,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        toast.success('Segment deleted');
        fetchSegmentsFromServer();
      } else {
        toast.error(data.error || 'Failed to delete segment');
        setSegments(previousSegments);
      }
    } catch (error) {
      console.error('Error deleting segment:', error);
      toast.error('Failed to delete segment');
      setSegments(previousSegments);
    }
  };

  const segmentRows = segments.sort((a, b) => {
    const dateA = new Date(b.updated_at || b.created_at || 0);
    const dateB = new Date(a.updated_at || a.created_at || 0);
    return dateA - dateB;
  });

  return (
    <Box sx={{ p: 3 }}>
      <ToastContainer />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={3}>
        <Box>
          <Typography variant="h4">Segments</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage email segments for targeted campaigns.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="contained" startIcon={<FaPlus />} onClick={openNewSegmentModal}>
            Add Segment
          </Button>
        </Stack>
      </Stack>

      {/* Customer Filter - Only show for role 1 (admin) */}
      {userRole === 1 && (
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center', 
          mb: 3,
          p: 2,
          bgcolor: '#f5f5f5',
          borderRadius: 1
        }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Filter by Customer</InputLabel>
            <Select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              label="Filter by Customer"
              size="small"
            >
              <MenuItem value="">All Customers</MenuItem>
              {filterCustomers
                .filter(customer => customer.id !== 0)
                .map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.username} ({customer.email})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Button 
            variant="contained"
            onClick={handleFilter}
            sx={{ height: '40px' }}
          >
            Filter
          </Button>
          {customerFilter !== '' && (
            <Button 
              variant="outlined"
              color="warning"
              onClick={handleClearFilter}
              sx={{ height: '40px' }}
            >
              Clear Filter
            </Button>
          )}
          <Button 
            variant="outlined"
            onClick={() => fetchSegmentsFromServer(customerFilter)}
            sx={{ height: '40px' }}
          >
            Refresh
          </Button>
        </Box>
      )}

      <Paper elevation={1} sx={{ mb: 4 }}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {userRole === 1 && <TableCell>Customer</TableCell>}
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {segmentRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={userRole === 1 ? 4 : 3} align="center">
                    No segments added yet.
                  </TableCell>
                </TableRow>
              )}
              {segmentRows.map((segment) => (
                <TableRow key={segment.id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{segment.name}</Typography>
                  </TableCell>
                  {userRole === 1 && (
                    <TableCell>
                      {
                        segment.customer_id === 0 || segment.customer_id === null || segment.customer_id === undefined
                          ? 'Common (No Customer)'
                          : segment.customer_username
                            ? (
                              <>
                                {segment.customer_username}
                                {segment.customer_email && (
                                  <Typography variant="caption" display="block" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                    {segment.customer_email}
                                  </Typography>
                                )}
                              </>
                            )
                            : '-'
                      }
                    </TableCell>
                  )}
                  <TableCell>{segment.updated_at ? new Date(segment.updated_at).toLocaleString() : (segment.created_at ? new Date(segment.created_at).toLocaleString() : '-')}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Contact List">
                      <IconButton 
                        color="info" 
                        onClick={() => handleViewContactList(segment)} 
                        sx={{ mr: 1 }}
                        disabled={!segment.sqlquery}
                      >
                        <FaEye />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Segment">
                      <IconButton color="primary" onClick={() => openEditSegmentModal(segment)} sx={{ mr: 1 }}>
                        <FaEdit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete segment">
                      <IconButton color="error" onClick={() => handleDeleteSegment(segment.id)}>
                        <FaTrash />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Segment Modal */}
      <Modal open={segmentModalOpen} onClose={() => setSegmentModalOpen(false)}>
        <Box component="form" onSubmit={handleSegmentSubmit} sx={modalStyle}>
          <Typography variant="h6" mb={2}>
            {segmentForm.id ? 'Edit Segment' : 'Add Segment'}
          </Typography>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel id="segment-customer-label">Customer</InputLabel>
                <Select
                  value={
                    segmentForm.customer_id !== undefined && segmentForm.customer_id !== null
                      ? segmentForm.customer_id
                      : ''
                  }
                  onChange={e => {
                    const value = e.target.value === '' ? '' : e.target.value;
                    handleSegmentFormChange('customer_id', value);
                  }}
                  label="Customer"
                  disabled={userRole === 2} // Disable for role 2 users (only their own account; matches template.js)
                >
                  {customers.length > 0 ? (
                    customers
                      .filter(c => c.id !== 0)
                      .map(c => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.username} ({c.email})
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem disabled>Loading customers...</MenuItem>
                  )}
                </Select>
              </FormControl>
              <TextField
                label="Segment name"
                value={segmentForm.name}
                onChange={(event) => handleSegmentFormChange('name', event.target.value)}
                required
                sx={{ flex: 1 }}
              />
            </Stack>

            {/* Tags & Events Section */}
            {segmentForm.customer_id && segmentForm.customer_id !== 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Tags & Events
                </Typography>
                
                <Grid container spacing={2}>
                  {/* Tags Multi-Select */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="segment-tags-label">Tags</InputLabel>
                      <Select
                        labelId="segment-tags-label"
                        multiple
                        value={segmentForm.tag_ids || []}
                        onChange={(event) => handleSegmentFormChange('tag_ids', event.target.value)}
                        label="Tags"
                        renderValue={(selected) => (
                          <Stack direction="row" flexWrap="wrap" gap={1}>
                            {selected.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">No tags selected</Typography>
                            ) : (
                              selected.map((tagId) => {
                                const tag = tags.find((t) => parseInt(t.id, 10) === parseInt(tagId, 10));
                                return <Chip key={tagId} label={tag?.tag || tagId} size="small" />;
                              })
                            )}
                          </Stack>
                        )}
                      >
                        {tags.length === 0 ? (
                          <MenuItem disabled>No tags available for this customer</MenuItem>
                        ) : (
                          tags.map((tag) => (
                            <MenuItem key={tag.id} value={parseInt(tag.id, 10)}>
                              {tag.tag}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Events Multi-Select */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="segment-events-label">Events</InputLabel>
                      <Select
                        labelId="segment-events-label"
                        multiple
                        value={segmentForm.event_ids || []}
                        onChange={(event) => handleSegmentFormChange('event_ids', event.target.value)}
                        label="Events"
                        renderValue={(selected) => (
                          <Stack direction="row" flexWrap="wrap" gap={1}>
                            {selected.map((eventId) => {
                              const event = events.find((e) => e.id === eventId);
                              return <Chip key={eventId} label={event ? `${event.name} (${event.key})` : eventId} size="small" />;
                            })}
                          </Stack>
                        )}
                      >
                        {events.length === 0 ? (
                          <MenuItem disabled>No events available for this customer</MenuItem>
                        ) : (
                          events.map((event) => (
                            <MenuItem key={event.id} value={parseInt(event.id, 10)}>
                              {event.name} ({event.key})
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Date Range Section */}
            {segmentForm.customer_id && segmentForm.customer_id !== 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Date Range
                </Typography>
                
                <Grid container spacing={2}>
                  {/* Start Date */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start Date"
                      value={segmentForm.start_date || ''}
                      onChange={(event) => handleSegmentFormChange('start_date', event.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>

                  {/* End Date */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="End Date"
                      value={segmentForm.end_date || ''}
                      onChange={(event) => handleSegmentFormChange('end_date', event.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      error={segmentForm.start_date && !segmentForm.end_date}
                      helperText={segmentForm.start_date && !segmentForm.end_date ? 'End date is required when start date is selected' : ''}
                    />
                  </Grid>

                  {/* Apply To Dropdown */}
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Apply To</InputLabel>
                      <Select
                        value={segmentForm.date_range_apply_to || 'Contact create'}
                        onChange={(event) => handleSegmentFormChange('date_range_apply_to', event.target.value)}
                        label="Apply To"
                      >
                        <MenuItem value="Contact create">Contact create</MenuItem>
                        <MenuItem value="Tags assigned">Tags assigned</MenuItem>
                        <MenuItem value="Events received">Events received</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Conditional Statements Section */}
            {segmentForm.customer_id && segmentForm.customer_id !== 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Conditional Statements
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Optional: Records are fetched when at least one of the conditions matches, using OR logic.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FaPlus />}
                    onClick={addConditionalStatement}
                  >
                    Add Condition
                  </Button>
                </Box>
                
                {segmentForm.conditional_statements && segmentForm.conditional_statements.length > 0 ? (
                  <Stack spacing={2}>
                    {segmentForm.conditional_statements.map((condition, index) => (
                      <Paper key={index} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                        <Grid container spacing={2} alignItems="center">
                          {/* Action Type Dropdown */}
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Action Type</InputLabel>
                              <Select
                                value={condition.action_type || ''}
                                onChange={(e) => updateConditionalStatement(index, 'action_type', e.target.value)}
                                label="Action Type"
                              >
                                <MenuItem value="Has Opened">Has Opened</MenuItem>
                                <MenuItem value="Has Clicked">Has Clicked</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Aggregation Dropdown */}
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Aggregation</InputLabel>
                              <Select
                                value={condition.aggregation || ''}
                                onChange={(e) => updateConditionalStatement(index, 'aggregation', e.target.value)}
                                label="Aggregation"
                              >
                                <MenuItem value="Count">Count</MenuItem>
                                <MenuItem value="Sum">Sum</MenuItem>
                                <MenuItem value="Avg">Avg</MenuItem>
                                <MenuItem value="Percentage">Percentage</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Operator Dropdown */}
                          <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Operator</InputLabel>
                              <Select
                                value={condition.operator || ''}
                                onChange={(e) => updateConditionalStatement(index, 'operator', e.target.value)}
                                label="Operator"
                              >
                                <MenuItem value="equal to">Equal to</MenuItem>
                                <MenuItem value="less than">Less than</MenuItem>
                                <MenuItem value="greater than">Greater than</MenuItem>
                                <MenuItem value="less than equal to">Less than equal to</MenuItem>
                                <MenuItem value="greater than equal to">Greater than equal to</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Value Input */}
                          <Grid item xs={12} sm={6} md={2}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Value"
                              value={condition.value || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                // Only allow integers
                                if (val === '' || /^\d+$/.test(val)) {
                                  updateConditionalStatement(index, 'value', val);
                                }
                              }}
                              inputProps={{ min: 0, step: 1 }}
                            />
                          </Grid>

                          {/* Remove Button */}
                          <Grid item xs={12} sm={6} md={2}>
                            <Button
                              fullWidth
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<FaTrash />}
                              onClick={() => removeConditionalStatement(index)}
                            >
                              Remove
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No conditional statements added. Click "Add Condition" to add one.
                  </Typography>
                )}
              </Box>
            )}

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={() => setSegmentModalOpen(false)}>Cancel</Button>
              <Button type="submit" variant="contained">
                {segmentForm.id ? 'Update Segment' : 'Save Segment'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      {/* Contact List Modal */}
      <Modal
        open={contactListModalOpen}
        onClose={handleCloseContactListModal}
        aria-labelledby="contact-list-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2">
              Contact List - {selectedSegmentForContactList?.name || 'Segment'}
            </Typography>
            <Button onClick={handleCloseContactListModal} variant="outlined" size="small">
              Close
            </Button>
          </Box>

          {contactListLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>First Name</TableCell>
                      <TableCell>Last Name</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contactListData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No contacts found
                        </TableCell>
                      </TableRow>
                    ) : (
                      contactListData.map((contact, index) => {
                        // Calculate sequential row number based on current page
                        const rowNumber = (contactListCurrentPage - 1) * contactListItemsPerPage + index + 1;
                        return (
                          <TableRow key={contact.id} hover>
                            <TableCell>{rowNumber}</TableCell>
                            <TableCell>{contact.email || '-'}</TableCell>
                            <TableCell>{contact.first_name || '-'}</TableCell>
                            <TableCell>{contact.last_name || '-'}</TableCell>
                            <TableCell>
                              {contact.create_date 
                                ? new Date(contact.create_date).toLocaleDateString() 
                                : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {contactListTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing page {contactListCurrentPage} of {contactListTotalPages} | Total Records: {contactListTotalRecords}
                  </Typography>
                  <Pagination
                    count={contactListTotalPages}
                    page={contactListCurrentPage}
                    onChange={handleContactListPageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}

              {contactListTotalPages === 1 && contactListTotalRecords > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Total Records: {contactListTotalRecords}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default Segments;
